import React from 'react';

/**
 * Shared order summary modal (restaurant owner, waiter, chef — same read-only view).
 */
const OrderDetailModal = ({ order, onClose }) => {
  if (!order) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-xl shadow-2xl border border-stone-200 max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="global-order-detail-title"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-3 sm:px-5 sm:py-3 border-b border-stone-200 bg-stone-50/95 backdrop-blur">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-500 text-white shadow-sm">
              <span className="text-base" aria-hidden>
                📋
              </span>
            </div>
            <div className="min-w-0">
              <p
                id="global-order-detail-title"
                className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500"
              >
                Order details
              </p>
              <p className="mt-0.5 text-sm font-semibold text-stone-800 truncate">
                Table {order.tableId?.tableNumber || 'Counter'} ·{' '}
                {order.orderDate ? new Date(order.orderDate).toLocaleString() : '—'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 hover:bg-stone-100 hover:text-stone-800 transition-colors"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 pb-4 pt-3 sm:px-5 sm:pt-4 sm:pb-5 space-y-4">
          <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-gradient-to-br from-white to-stone-50/80 p-3 sm:p-4 shadow-sm">
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-orange-400 to-orange-500 rounded-l-xl" />
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 mb-1.5 pl-1">
              Customer
            </p>
            <p className="font-semibold text-stone-800 pl-1">{order.customerName || '—'}</p>
            {order.mobileNumber && (
              <p className="text-xs text-stone-600 mt-0.5 pl-1">{order.mobileNumber}</p>
            )}
            <p className="text-[11px] text-stone-500 mt-2 pl-1">
              🪑{' '}
              {order.tableId?.tableNumber ? `Table ${order.tableId.tableNumber}` : 'Counter order'}
            </p>
          </div>

          <div className="rounded-xl border border-stone-200 bg-white overflow-hidden shadow-sm">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500 px-3 py-2 border-b border-stone-100">
              Ordered items ({order.items?.length || 0})
            </p>
            <ul className="divide-y divide-stone-100">
              {order.items?.map((item, idx) => (
                <li key={idx} className="flex justify-between items-center px-3 py-2.5 text-sm">
                  <span className="text-stone-800">
                    {item.name} <span className="text-stone-500">×{item.quantity}</span>
                  </span>
                  <span className="font-semibold text-emerald-700">
                    ₹{(item.price * item.quantity).toFixed(2)}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="rounded-xl border border-emerald-100 bg-emerald-50/50 p-3 sm:p-4 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-800/80 mb-1">
              Bill summary
            </p>
            <div className="flex justify-between text-xs text-stone-600">
              <span>Subtotal</span>
              <span className="font-medium text-stone-800">₹{(order.totalAmount ?? 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-600">
              <span>Tax ({order.taxPercentage ?? 0}%)</span>
              <span className="font-medium text-stone-800">₹{(order.taxAmount || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-xs text-stone-600">
              <span>Platform fees</span>
              <span className="font-medium text-stone-800">₹{(order.serviceAmount || 0).toFixed(2)}</span>
            </div>
            {order.offerDiscount > 0 && (
              <div className="flex justify-between text-xs text-emerald-700">
                <span>Discount</span>
                <span className="font-semibold">−₹{order.offerDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between items-center pt-2 mt-2 border-t-2 border-emerald-200">
              <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">Total</span>
              <span className="text-base font-bold text-emerald-800">
                ₹{(order.finalAmount || 0).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 z-10 border-t border-stone-200 bg-white/95 backdrop-blur-sm px-4 py-3 sm:px-5 sm:py-3">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center rounded-full bg-stone-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-stone-800 hover:shadow-md transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailModal;
